import { Injectable, inject } from '@angular/core';
import {
  CommentsApi,
  ResponseError,
  type CommentAttachmentDownloadUrl,
} from '@tourfold/libraries/tf-api-client-public-v2';

/**
 * Why uploads can fail. Each value maps 1:1 to a translation key in the consumer
 * (`COMMENTS.ATTACHMENT_ERROR.<REASON>`) so the editor / banner can surface a tailored message.
 *  - `mime-not-allowed` — server rejected the MIME against the allow-list (SVG always blocked).
 *  - `too-large` — file exceeds `comments.attachments.max-bytes-per-file` (default 25 MiB).
 *  - `storage-limit-exceeded` — the tenant is over its storage allowance; user must free space.
 *  - `too-many-attachments` — comment already has `comments.attachments.max-per-comment` (10) chips.
 *  - `upload-rejected` — the presigned-POST to object storage returned a non-2xx.
 *  - `upload-timeout` — the presigned-POST hit our 120 s safety abort.
 *  - `aborted` — caller-side abort via the user removing the pill mid-upload. Internal: this
 *    reason is never surfaced to the banner — `removePending` already cleaned up local state.
 *  - `unknown` — anything else (network failure, unrecognised problem-type).
 */
export type AttachmentUploadErrorReason =
  | 'mime-not-allowed'
  | 'too-large'
  | 'storage-limit-exceeded'
  | 'too-many-attachments'
  | 'upload-rejected'
  | 'upload-timeout'
  | 'aborted'
  | 'unknown';

/**
 * Typed error thrown by {@link CommentAttachmentsService.uploadFile}. The caller (Tiptap editor,
 * edit form, future drop overlay) inspects `reason` to decide what to render — `cause` keeps the
 * underlying error around for logging without leaking it into the user-facing message.
 */
export class AttachmentUploadError extends Error {
  override name = 'AttachmentUploadError';
  constructor(
    readonly reason: AttachmentUploadErrorReason,
    override readonly cause?: unknown,
  ) {
    super(reason);
  }
}

/**
 * Client-side mirror of the server's blocked MIME set (see
 * `backend/tourfold-api/features/comments-impl/src/main/kotlin/com/tourfold/comments/impl/domain/
 * CommentAttachmentLimits.kt`). Pre-rejecting these locally means a forbidden file never crosses
 * the network — same red-pill + banner UX as a server reject, but in milliseconds instead of a
 * round trip. **The server is still the source of truth**; a stale client list will still get
 * caught server-side. Keep this in sync when the backend list changes.
 */
export const BLOCKED_MIME_TYPES: ReadonlySet<string> = new Set([
  // XSS / script execution
  'image/svg+xml',
  'text/html',
  'text/htm',
  'application/xhtml+xml',
  'text/xml',
  'application/javascript',
  'application/x-javascript',
  'text/javascript',
  'text/jscript',
  'application/ecmascript',
  'text/ecmascript',
  'application/vbscript',
  'text/vbscript',
  'application/hta',
  'application/x-shockwave-flash',
  // Native executables
  'application/x-msdownload',
  'application/x-msdos-program',
  'application/vnd.microsoft.portable-executable',
  'application/x-executable',
  'application/x-mach-binary',
  'application/x-msmetafile',
  'application/java-archive',
  'application/x-java-archive',
  // Installers and disk images
  'application/x-msi',
  'application/vnd.ms-cab-compressed',
  'application/vnd.apple.installer+xml',
  'application/x-apple-diskimage',
  'application/x-iso9660-image',
  'application/x-deb',
  'application/x-rpm',
  // Shell scripts / interpreters
  'application/x-sh',
  'application/x-shellscript',
  'application/x-csh',
  'application/x-ksh',
  'application/x-bat',
  'application/x-perl',
  'application/x-python',
]);

/**
 * Returns `true` when the given MIME is on the client-side blocklist. The check is case-
 * insensitive and tolerant of trailing whitespace, matching the server's `isBlocked` semantics.
 * An empty / undefined MIME returns `false` — the browser couldn't determine the type, the
 * server-side check will catch known-dangerous types via the same fallback path.
 */
export function isBlockedMime(mimeType: string | undefined | null): boolean {
  if (!mimeType) return false;
  return BLOCKED_MIME_TYPES.has(mimeType.trim().toLowerCase());
}

/**
 * Wraps the presigned-POST upload pipeline for comment attachments.
 *
 * Flow:
 *  1. `POST /api/v2/comments/attachments/uploads` returns a presigned form descriptor
 *     (`upload_url` + `upload_fields` + `filestore_file_id`).
 *  2. The client POSTs the bytes to `upload_url` as `multipart/form-data`, with every
 *     `upload_fields` entry appended in order *before* the `file` part (S3 policy requirement).
 *  3. The returned `filestoreFileId` is then referenced in a subsequent `POST /api/v2/comments`
 *     or `PATCH /api/v2/comments/{id}` via `attachment_ids`. No `so_comment_attachments` row is
 *     created at upload time — orphans are reaped by the backend cleanup cron.
 *
 * Mirrors the case-attachment upload pattern (`AbortController` + 120 s safety abort) so the
 * Tiptap editor doesn't hang forever on a stalled S3 connection.
 */
@Injectable({ providedIn: 'root' })
export class CommentAttachmentsService {
  private readonly api = inject(CommentsApi);

  /** Safety abort on the presigned POST. Matches the case-attachment upload dialog's budget. */
  static readonly UPLOAD_TIMEOUT_MS = 120_000;

  /**
   * Uploads a single file and resolves with the `filestoreFileId` the caller passes to comment
   * create / edit. Throws {@link AttachmentUploadError} with a structured reason on any failure
   * the user should be told about (MIME / size / storage / object-storage POST failures).
   *
   * `options.onIntent` fires once, synchronously after the upload-intent step resolves but
   * before the bytes start uploading. This lets the caller stash the `filestoreFileId` on a
   * pending pill *during* the upload — necessary so a mid-upload pill removal can fire the
   * abandoned-upload DELETE on the server side (otherwise the id is only known when the whole
   * `uploadFile` promise resolves, by which point the user-cancel has already happened).
   *
   * `options.signal` aborts both the in-flight intent-fetch and the presigned-POST. The 120 s
   * safety timeout still fires in parallel via the internal controller; whichever signals
   * first wins. Aborted uploads throw `AttachmentUploadError('aborted', cause)` — callers
   * should distinguish that from `upload-timeout` (system-driven) and `upload-rejected`
   * (server-driven) since 'aborted' is a deliberate user action and doesn't deserve a banner.
   */
  async uploadFile(
    file: File,
    options?: {
      onIntent?: (intent: { filestoreFileId: string }) => void;
      signal?: AbortSignal;
    },
  ): Promise<{ filestoreFileId: string }> {
    let intent;
    try {
      intent = await this.api.createCommentAttachmentUploadIntent({
        createCommentAttachmentUploadIntent: {
          originalFileName: file.name,
          // Fall back to a generic binary MIME if the browser couldn't determine one — the server
          // will reject it cleanly against the allow-list, which is the right UX (user gets a MIME
          // error, not a generic "unknown failure").
          mimeType: file.type || 'application/octet-stream',
          // OpenAPI maps Java `Long` to TypeScript `string`; serialize numerically.
          sizeBytes: String(file.size),
        },
      });
    } catch (err) {
      throw new AttachmentUploadError(await mapIntentError(err), err);
    }

    // Notify the caller of the id immediately so a mid-upload pill removal has something to
    // delete server-side. Fired synchronously here; consumers don't have to await.
    options?.onIntent?.({ filestoreFileId: intent.filestoreFileId });

    try {
      await postToPresigned(intent.uploadUrl, intent.uploadFields, file, options?.signal);
    } catch (err) {
      const reason: AttachmentUploadErrorReason =
        err instanceof DOMException && err.name === 'AbortError'
          // Distinguish caller-abort (user removed the pill) from the internal safety-timeout:
          // the caller's signal aborted means it's a deliberate cancel; otherwise it was the
          // 120 s safety guard that fired.
          ? options?.signal?.aborted ? 'aborted' : 'upload-timeout'
          : 'upload-rejected';
      throw new AttachmentUploadError(reason, err);
    }

    return { filestoreFileId: intent.filestoreFileId };
  }

  /**
   * Refreshes the short-lived presigned URLs for a single attachment. Used by the preview-modal
   * open path (the URL on the comment list expires after 5 min; the modal can open hours later).
   *
   * Throws on a removed blob: the server returns **410 Gone** with
   * `comments/attachment-blob-gone` when the underlying filestore blob has been hard-deleted
   * since the comment was loaded, and the API client propagates that as a rejected Promise.
   * Callers render the "attachment unavailable" state in their `catch` path. 404
   * `comments/attachment-not-found` is the corresponding "no record of this id" case.
   */
  fetchDownloadUrl(filestoreFileId: string): Promise<CommentAttachmentDownloadUrl> {
    return this.api.refreshCommentAttachmentDownloadUrl({ filestoreFileId });
  }

  /**
   * Marks an uploaded-but-not-yet-linked comment attachment as deleted on the server so storage
   * is reclaimed immediately, rather than waiting for the nightly orphan-cleanup task. Called
   * from the editor's pill-✕ handler whenever the pill carries a `filestoreFileId` (i.e. the
   * upload-intent step has resolved, even if the bytes are still in flight).
   *
   * Failure handling is the caller's: the server returns
   *  - **204** on success (or as an idempotent no-op on already-deleted files),
   *  - **404 `comments/attachment-not-found`** when the id is unknown or belongs to a different
   *    feature's source key — both benign, the file was already gone or never ours,
   *  - **409 `comments/attachment-already-linked`** when the file has been referenced by a
   *    posted comment — happens on a submit + remove race, also benign for the caller since
   *    the linked file is the user's actual posted content,
   *  - any network error — the reaper sweeps the orphan eventually.
   *
   * Callers should `.catch(() => {})` swallow these; the reaper is the safety net.
   */
  deleteAbandonedUpload(filestoreFileId: string): Promise<void> {
    return this.api.deleteCommentAttachmentUpload({ filestoreFileId });
  }
}

async function postToPresigned(
  uploadUrl: string,
  uploadFields: Record<string, string>,
  file: File,
  callerSignal?: AbortSignal,
): Promise<void> {
  const formData = new FormData();
  // Order matters: every policy entry must appear before `file` in the multipart body.
  for (const [key, value] of Object.entries(uploadFields)) {
    formData.append(key, value);
  }
  formData.append('file', file);

  // The internal controller carries the 120 s safety timeout. When the caller passes its own
  // signal, we forward its abort into the internal controller so the fetch aborts on either
  // path. The thrown DOMException is then disambiguated by inspecting `callerSignal.aborted`
  // in the catch above (caller-abort → 'aborted'; timeout-abort → 'upload-timeout').
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    CommentAttachmentsService.UPLOAD_TIMEOUT_MS,
  );
  const onCallerAbort = () => controller.abort();
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    }
  }

  try {
    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }
  } finally {
    clearTimeout(timeoutId);
    callerSignal?.removeEventListener('abort', onCallerAbort);
  }
}

async function mapIntentError(err: unknown): Promise<AttachmentUploadErrorReason> {
  if (!(err instanceof ResponseError)) return 'unknown';
  try {
    const body = (await err.response.clone().json()) as { type?: unknown };
    const type = typeof body?.type === 'string' ? body.type : '';
    // The backend's problem-type URIs end in the slug — match on suffix so we're robust to the
    // configured base ('https://problems.tourfold.com/comments/attachment-...' vs a future rename).
    if (type.endsWith('/attachment-mime-not-allowed')) return 'mime-not-allowed';
    if (type.endsWith('/attachment-too-large')) return 'too-large';
    if (type.endsWith('/attachment-storage-limit-exceeded')) return 'storage-limit-exceeded';
    if (type.endsWith('/too-many-attachments')) return 'too-many-attachments';
  } catch {
    // Body wasn't JSON / didn't parse — fall through to unknown.
  }
  return 'unknown';
}
